import { Chat } from '@/config'
import { MessageProps } from '../types'
import { createStyles } from '@codeleap/styles'
import { DEFAULT_REACTIONS, isMessageDeleted } from '@packages/chat'
import React, { useState, useRef, useEffect, useCallback, memo } from 'react'
import { View, Icon, ActionIcon, ReactionPickerModal, Emoji } from '@/components'
import { useChannelStateContext, useMessageContext } from 'stream-chat-react'

export type EmojiReactionButtonProps = Pick<MessageProps, 'threadList'>

const EmojiReactionButtonInner = ({ threadList }: EmojiReactionButtonProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isPositioned, setIsPositioned] = useState(false)
  const [tooltipPosition, setTooltipPosition] = useState<'top' | 'bottom'>('top')
  const [tooltipHorizontalAlign, setTooltipHorizontalAlign] = useState<'left' | 'right' | 'center'>('center')
  const buttonRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const { message } = useMessageContext()
  const { channel } = useChannelStateContext()

  const selectModeActive = Chat.Store.useSelectMode({ threadList }).selectModeActive

  const myReactions = message?.own_reactions || []

  const calculateTooltipPosition = useCallback(() => {
    if (!buttonRef.current || !tooltipRef.current) return

    const buttonRect = buttonRef.current.getBoundingClientRect()
    const tooltipRect = tooltipRef.current.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const viewportWidth = window.innerWidth

    const spaceAbove = buttonRect.top
    const spaceBelow = viewportHeight - buttonRect.bottom
    setTooltipPosition(spaceBelow > spaceAbove ? 'bottom' : 'top')

    const tooltipWidth = tooltipRect.width || 292 // Use actual width or fallback to known width
    const buttonCenterX = buttonRect.left + (buttonRect.width / 2)
    const viewportPadding = 16

    const centerLeft = buttonCenterX - (tooltipWidth / 2)
    const centerRight = centerLeft + tooltipWidth

    const leftAlignedLeft = buttonRect.left
    const leftAlignedRight = leftAlignedLeft + tooltipWidth

    const rightAlignedRight = buttonRect.right
    const rightAlignedLeft = rightAlignedRight - tooltipWidth

    const centerFits = centerLeft >= viewportPadding && centerRight <= (viewportWidth - viewportPadding)
    const leftFits = leftAlignedLeft >= viewportPadding && leftAlignedRight <= (viewportWidth - viewportPadding)
    const rightFits = rightAlignedLeft >= viewportPadding && rightAlignedRight <= (viewportWidth - viewportPadding)

    if (centerFits) {
      setTooltipHorizontalAlign('center')
    } else if (leftFits) {
      setTooltipHorizontalAlign('left')
    } else if (rightFits) {
      setTooltipHorizontalAlign('right')
    } else {
      const centerOverflow = Math.max(0, viewportPadding - centerLeft) + Math.max(0, centerRight - (viewportWidth - viewportPadding))
      const leftOverflow = Math.max(0, viewportPadding - leftAlignedLeft) + Math.max(0, leftAlignedRight - (viewportWidth - viewportPadding))
      const rightOverflow = Math.max(0, viewportPadding - rightAlignedLeft) + Math.max(0, rightAlignedRight - (viewportWidth - viewportPadding))

      if (leftOverflow <= centerOverflow && leftOverflow <= rightOverflow) {
        setTooltipHorizontalAlign('left')
      } else if (rightOverflow <= centerOverflow) {
        setTooltipHorizontalAlign('right')
      } else {
        setTooltipHorizontalAlign('center')
      }
    }

    setIsPositioned(true)
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        buttonRef.current &&
        !tooltipRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    const handleScroll = () => {
      if (isOpen) {
        setIsOpen(false)
      }
    }

    let mousedownTimeout: NodeJS.Timeout
    let scrollTimeout: NodeJS.Timeout

    if (isOpen) {
      document.body.style.overflow = 'hidden'

      mousedownTimeout = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside)
      }, 50)

      scrollTimeout = setTimeout(() => {
        document.addEventListener('scroll', handleScroll, true)
      }, 50)

      setTimeout(() => {
        calculateTooltipPosition()
      }, 0)
    } else {
      setIsPositioned(false)
    }

    return () => {
      clearTimeout(mousedownTimeout)
      clearTimeout(scrollTimeout)
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('scroll', handleScroll, true)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, calculateTooltipPosition])

  const handleReaction = useCallback(
    async (emoji: string) => {
      await Chat.Services.handleReaction({ message, channel, emoji })
      setIsOpen(false)
      Promise.resolve().then(() => {
        const { APIClient } = require('@/services')
        APIClient.Chat.invalidateReactionsCache(message.id)
      })
    },
    [message, channel],
  )

  const toggleTooltip = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setIsOpen(!isOpen)
  }, [isOpen])

  const handleOpenPicker = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setIsOpen(false)
    ReactionPickerModal.open({ message, channel })
  }, [message, channel])

  if (selectModeActive) return null
  if (isMessageDeleted(message)) return null

  return (
    <View
      style={styles.container}
      onClick={(e) => e.stopPropagation()}
    >
      <View style={styles.buttonWrapper}>
        <div ref={buttonRef}>
          <ActionIcon
            icon='face-smile'
            style={[
              { touchableWrapper: styles.button },
              { icon: styles.icon },
              isOpen && styles.buttonActive,
            ]}
            className='emoji-reaction-button'
            onClick={toggleTooltip}
          />
        </div>

        {isOpen && (
          <View
            ref={tooltipRef}
            style={{
              ...styles.tooltip,
              ...(tooltipPosition === 'top' ? styles.tooltipTop : styles.tooltipBottom),
              ...(tooltipHorizontalAlign === 'left' ? styles.tooltipLeft :
                tooltipHorizontalAlign === 'right' ? styles.tooltipRight :
                  styles.tooltipCenter),
              opacity: isPositioned ? 1 : 0,
              transition: isPositioned ? 'opacity 0.15s ease' : 'none',
            }}
          >
            <View style={styles.emojisContainer}>
              {DEFAULT_REACTIONS.map((reaction, index) => {
                const isSelected = myReactions.some(r => r.emoji_code === reaction.emoji)
                return (
                  <Emoji
                    key={index}
                    emoji={reaction.emoji}
                    isSelected={isSelected}
                    onPress={handleReaction}
                    size={24}
                    style={styles.emoji}
                    debugName={`Reaction:${reaction.emoji}`}
                  />
                )
              })}
              <View
                style={styles.addButton}
                onClick={handleOpenPicker}
              >
                <Icon
                  name='plus'
                  style={styles.addIcon}
                />
              </View>
            </View>
          </View>
        )}
      </View>
    </View>
  )
}

const styles = createStyles((theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 8,
    marginRight: 8,
  },
  buttonWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  button: {
    width: 24,
    height: 24,
    borderRadius: theme.radius.infinite,
    backgroundColor: theme.colors.buttonSubtleSecondaryBgDefault,
    border: `1px solid ${theme.baseColors.neutralBlackTransparent100}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    opacity: 0,
    visibility: 'hidden',
    '&:hover': {
      backgroundColor: theme.colors.rowBgHover,
      transform: 'scale(1.1)',
    },
  },
  buttonActive: {
    backgroundColor: theme.colors.rowBgHover,
    opacity: 1,
    visibility: 'visible',
  },
  icon: {
    width: 16,
    height: 16,
    color: theme.colors.textCaption,
    fill: theme.colors.textCaption,
    stroke: 'none',
    strokeWidth: 0,
  },
  tooltip: {
    position: 'absolute',
    backgroundColor: theme.baseColors.primarySolid100,
    borderRadius: theme.radius.large,
    boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
    padding: theme.spacing.value(1),
    zIndex: 1000,
  },
  tooltipTop: {
    bottom: '100%',
    marginBottom: theme.spacing.value(1),
  },
  tooltipBottom: {
    top: '100%',
    marginTop: theme.spacing.value(1),
  },
  tooltipLeft: {
    left: 0,
    right: 'auto',
  },
  tooltipRight: {
    right: 0,
    left: 'auto',
  },
  tooltipCenter: {
    left: '50%',
    transform: 'translateX(-50%)',
  },
  emojisContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing.value(0.5),
    alignItems: 'center',
  },
  emoji: {
    width: 36,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.radius.small,
    transition: 'background-color 0.2s ease, transform 0.2s ease',
    '&:hover': {
      backgroundColor: theme.colors.rowBgHover,
      transform: 'scale(1.2)',
    },
  },
  addButton: {
    width: 36,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.radius.infinite,
    cursor: 'pointer',
    backgroundColor: theme.baseColors.primarySolid50,
    transition: 'background-color 0.2s ease, transform 0.2s ease',
    '&:hover': {
      transform: 'scale(1.1)',
    },
  },
  addIcon: {
    width: 20,
    height: 20,
    color: theme.colors.buttonHighlightSecondaryIconDefault,
  },
}))

export const EmojiReactionButton = memo(EmojiReactionButtonInner, (prev, next) => {
  return prev.threadList === next.threadList
})

